import React, { useState } from 'react';
import {
  FiUpload as Upload,
  FiFile as File,
  FiImage as Image,
  FiX as X,
  FiCheck as Check,
  FiAlertCircle as AlertCircle,
  FiFileText as FileText,
  FiCamera as Camera,
  FiPaperclip as Paperclip
} from 'react-icons/fi';

const ClaimDocumentUpload = ({ claimId, onUploadComplete, maxFiles = 10 }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);

  const acceptedFileTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.heic', '.heif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  };

  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    if (files.length + newFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const validFiles = newFiles.filter(file => {
      if (file.size > maxFileSize) {
        alert(`${file.name} is too large. Maximum size is 10MB`);
        return false;
      }
      return true;
    });

    const filesWithMetadata = validFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: 'pending',
      uploadedAt: null
    }));

    setFiles(prev => [...prev, ...filesWithMetadata]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
    
    // Remove progress for this file
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);

    // Simulate upload process for each file
    for (const fileData of files) {
      if (fileData.status === 'uploaded') continue;

      try {
        // Update status to uploading
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, status: 'uploading' } : f
        ));

        // Simulate upload progress
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setUploadProgress(prev => ({
            ...prev,
            [fileData.id]: progress
          }));
        }

        // Simulate successful upload
        // In real app, this would upload to Supabase Storage
        const uploadResult = {
          id: fileData.id,
          claimId,
          filename: fileData.name,
          fileType: fileData.type,
          fileSize: fileData.size,
          storagePath: `claims/${claimId}/${fileData.id}-${fileData.name}`,
          uploadedAt: new Date().toISOString()
        };


        // Update status to uploaded
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'uploaded', uploadedAt: uploadResult.uploadedAt } 
            : f
        ));

      } catch (error) {
        console.error('Upload error:', error);
        setFiles(prev => prev.map(f => 
          f.id === fileData.id ? { ...f, status: 'error' } : f
        ));
      }
    }

    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(files.filter(f => f.status === 'uploaded'));
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return Image;
    if (fileType === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const allFilesUploaded = files.length > 0 && files.every(f => f.status === 'uploaded');
  const hasErrors = files.some(f => f.status === 'error');

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 ${
          dragActive
            ? 'border-[#DC143C] bg-red-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept={Object.keys(acceptedFileTypes).join(',')}
          onChange={handleFileInput}
          className="hidden"
          disabled={uploading || files.length >= maxFiles}
        />

        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Upload size={32} className="text-gray-600" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Upload Claim Documents
          </h3>
          
          <p className="text-gray-600 text-center mb-4">
            Drag and drop files here, or click to browse
          </p>

          <div className="flex flex-wrap gap-2 justify-center text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Image size={16} />
              Photos
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <FileText size={16} />
              PDFs
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <File size={16} />
              Documents
            </span>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Max {maxFiles} files • Up to 10MB each
          </p>
        </label>
      </div>

      {/* Quick Capture Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => document.getElementById('camera-capture').click()}
          className="flex items-center justify-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={uploading || files.length >= maxFiles}
        >
          <Camera size={20} className="text-gray-600" />
          <span className="font-medium text-gray-900">Take Photo</span>
          <input
            id="camera-capture"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileInput}
            className="hidden"
            disabled={uploading || files.length >= maxFiles}
          />
        </button>

        <button
          onClick={() => document.getElementById('file-upload').click()}
          className="flex items-center justify-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={uploading || files.length >= maxFiles}
        >
          <Paperclip size={20} className="text-gray-600" />
          <span className="font-medium text-gray-900">Choose Files</span>
        </button>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">
              Selected Files ({files.length}/{maxFiles})
            </h4>
            {!uploading && !allFilesUploaded && (
              <button
                onClick={() => setFiles([])}
                className="text-sm text-gray-600 hover:text-red-600 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="space-y-2">
            {files.map((fileData) => {
              const FileIcon = getFileIcon(fileData.type);
              const progress = uploadProgress[fileData.id] || 0;

              return (
                <div
                  key={fileData.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Preview or Icon */}
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                      {fileData.preview ? (
                        <img
                          src={fileData.preview}
                          alt={fileData.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileIcon size={24} className="text-gray-600" />
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {fileData.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatFileSize(fileData.size)}
                          </div>
                        </div>

                        {/* Status Badge */}
                        {fileData.status === 'pending' && !uploading && (
                          <button
                            onClick={() => removeFile(fileData.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <X size={20} />
                          </button>
                        )}

                        {fileData.status === 'uploading' && (
                          <div className="text-sm font-medium text-blue-600">
                            {progress}%
                          </div>
                        )}

                        {fileData.status === 'uploaded' && (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check size={20} />
                            <span className="text-sm font-medium">Uploaded</span>
                          </div>
                        )}

                        {fileData.status === 'error' && (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertCircle size={20} />
                            <span className="text-sm font-medium">Failed</span>
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {fileData.status === 'uploading' && (
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {files.length > 0 && !allFilesUploaded && (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600">
            {files.filter(f => f.status === 'pending').length} file(s) ready to upload
          </div>
          <button
            onClick={uploadFiles}
            disabled={uploading || files.every(f => f.status === 'uploaded')}
            className="bg-[#DC143C] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#B01030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="spinner"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload size={20} />
                Upload All
              </>
            )}
          </button>
        </div>
      )}

      {/* Success Message */}
      {allFilesUploaded && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check size={20} className="text-green-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-green-900">
              All files uploaded successfully!
            </div>
            <div className="text-sm text-green-800 mt-1">
              Your documents have been securely uploaded and attached to your claim.
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {hasErrors && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-red-900">
              Some files failed to upload
            </div>
            <div className="text-sm text-red-800 mt-1">
              Please try uploading the failed files again.
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-3">Document Tips</h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="shrink-0">•</span>
            <span>Take clear, well-lit photos of all damage</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0">•</span>
            <span>Include photos from multiple angles</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0">•</span>
            <span>Upload any police reports or incident documentation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0">•</span>
            <span>Include receipts for any emergency repairs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0">•</span>
            <span>Contact information for any witnesses or other parties</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ClaimDocumentUpload;